import { upsertDocumentInItems } from './documents-store-state'
import type { Dependencies } from '../../types/Dependencies'
import type { Doc } from '../../types/Doc'
import type {
  DocumentsStoreApi,
  UpdateDocumentRequest,
} from './documents-store'

const MAX_UPDATE_ATTEMPTS = 3

type DocumentsSetState = DocumentsStoreApi['setState']
type DocumentsGetState = DocumentsStoreApi['getState']

export async function updateDocumentAction({
  dependencies,
  get,
  request,
  set,
}: {
  dependencies: Dependencies
  get: DocumentsGetState
  request: UpdateDocumentRequest
  set: DocumentsSetState
}) {
  const previousDocument = getPreviousDocument({
    documentId: request.document._id,
    get,
  })
  const optimisticDocument = {
    ...(previousDocument ?? request.document),
    ...request.values,
  }

  setDocumentOptimistically({ document: optimisticDocument, set })

  try {
    const response = await putDocumentPatchWithRetry({
      dependencies,
      documentId: request.document._id,
      values: request.values,
    })
    const savedDocument = response

    setDocumentOptimistically({ document: savedDocument, set })

    return savedDocument
  } catch (error) {
    rollbackOptimisticDocument({
      documentId: optimisticDocument._id,
      previousDocument,
      set,
    })

    throw error
  }
}

async function putDocumentPatchWithRetry({
  dependencies,
  documentId,
  values,
}: {
  dependencies: Dependencies
  documentId: string
  values: Record<string, string>
}) {
  for (let attempt = 0; attempt < MAX_UPDATE_ATTEMPTS; attempt += 1) {
    const latestDocument = await dependencies.records.get(documentId)

    try {
      return await dependencies.records.patch(
        documentId,
        Number.parseInt(latestDocument._rev, 10),
        values,
      )
    } catch (error) {
      if (!isConflictError(error) || attempt === MAX_UPDATE_ATTEMPTS - 1) {
        throw error
      }
    }
  }

  throw new Error('Document update failed after conflict retries.')
}

function isConflictError(error: unknown) {
  if (!error || typeof error !== 'object') return false

  return (
    ('status' in error && error.status === 409) ||
    ('statusCode' in error && error.statusCode === 409) ||
    ('name' in error && error.name === 'conflict')
  )
}

function getPreviousDocument({
  documentId,
  get,
}: {
  documentId: string
  get: DocumentsGetState
}) {
  return get().items.find((item) => item._id === documentId)
}

function setDocumentOptimistically({
  document,
  set,
}: {
  document: Doc
  set: DocumentsSetState
}) {
  set((state) => ({
    items: upsertDocumentInItems(state.items, document),
  }))
}

function rollbackOptimisticDocument({
  documentId,
  previousDocument,
  set,
}: {
  documentId: string
  previousDocument: Doc | undefined
  set: DocumentsSetState
}) {
  set((state) => ({
    items: previousDocument
      ? upsertDocumentInItems(state.items, previousDocument)
      : state.items.filter((item) => item._id !== documentId),
  }))
}
